// Fonction utilitaire pour nettoyer les champs fichier vides
// à ne pas envoyer dans les requêtes
export const cleanFileFields = (data, fileFields) => {
  const cleanedData = { ...data }
  // Ne pas supprimer les champs de fichier null, car ils indiquent une suppression
  // Nous ne supprimons que les champs undefined ou vides
  fileFields.forEach(
    fieldAttr => {
      if (data[fieldAttr] === undefined || data[fieldAttr] === '') {
        delete cleanedData[fieldAttr]
      }
    }
  )
  return cleanedData
}

// Fonction utilitaire pour détecter si un champ est un champ de fichier
export const isFileField = (key, value) => {
  // Vérifier si le nom du champ se termine par "_file"
  const hasFileNamePattern = key.endsWith('_file');
  
  // Vérifier si la valeur est une instance de File
  const isFileInstance = value instanceof File;
  
  // Vérifier si c'est un tableau contenant au moins un fichier ou un objet de fichier du backend
  const isFileArray = Array.isArray(value) && value.some(item => item instanceof File || isBackendFileObject(item));
  
  // Vérifier si c'est un objet de fichier provenant du backend
  const isFileObject = isBackendFileObject(value);
  
  // Un champ est un champ de fichier si son nom se termine par "_file"
  // OU si sa valeur est une instance de File
  // OU si c'est un tableau contenant au moins un fichier ou un objet de fichier du backend
  // OU si c'est un objet de fichier provenant du backend
  // OU si sa valeur est null (pour la suppression de fichier) ET son nom se termine par "_file"
  return hasFileNamePattern || isFileInstance || isFileArray || isFileObject || (value === null && hasFileNamePattern);
};

// Fonction pour détecter automatiquement les champs de fichier
export const detectFileFields = (data) => {
  return Object.keys(data).filter(key => isFileField(key, data[key]));
};

export const sanitizeFormData = (data) => {
  const { updated_at, created_at, id, archived_at, ...rest } = data
  return rest
}

/**
 * Convertit un tableau d'objets en format formData
 * @param {Object} data - L'objet contenant le tableau à transformer
 * @param {string} baseKey - La clé de base pour la transformation (ex: 'document[contacts]')
 * @param {Array} fileFields - (Optionnel) Liste explicite des champs de fichier
 * @returns {FormData} Un objet FormData avec la structure désirée
 */
export const transformToFormData = (data, baseKey, fileFields = []) => {
  const formData = new FormData()
  
  // Détecter automatiquement les champs de fichier si aucun n'est fourni
  const detectedFileFields = fileFields.length === 0 ? detectFileFields(data) : [];
  
  // Combiner les champs de fichier détectés et ceux fournis explicitement
  const allFileFields = [...new Set([...fileFields, ...detectedFileFields])];

  const cleanedData = cleanFileFields(data, allFileFields)

  Object.entries(cleanedData).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const formKey = `${baseKey}[${key}][]`
        if(item instanceof File) {
          formData.append(formKey, item)
        }
        // Si c'est un objet de fichier du backend avec attachable_sgid
        else if (isBackendFileObject(item)) {
          formData.append(formKey, item.attachable_sgid)
        }
        // Si l'élément est un objet (comme un locataire)
        else if (typeof item === 'object' && item !== null) {
          Object.entries(item).forEach(([subKey, subValue]) => {
            formData.append(`${formKey}[${subKey}]`, subValue)
          })
        }
        // Si c'est une valeur simple
        else {
          formData.append(`${baseKey}[${key}][]`, item)
        }
      })
    }
    // Cas 2: Traiter les valeurs null pour les champs de fichier
    else if (value === null) {
      // Si c'est un champ de fichier, on envoie explicitement la valeur null
      // pour indiquer qu'il faut supprimer le fichier
      const isFileField = key.endsWith('_file') || allFileFields.includes(key)
      if (isFileField) {
        formData.append(`${baseKey}[${key}]`, '')
      }
      // Sinon, on ignore les valeurs null comme avant
    }
    // Cas 3: Ignorer les valeurs undefined
    else if (value === undefined) {
      // Ne rien faire
    }
    // Cas 4: Traiter les objets de fichier provenant du backend
    else if (isBackendFileObject(value)) {
      // Pour les objets de fichier provenant du backend, on ne fait rien
      // car ils sont déjà enregistrés côté serveur
      // Si on veut les supprimer, on doit passer null explicitement
      // TODO à vérifier : Attention peut être qu'il sera pertinent d'envoyer le attachable_sgid s'il est présent
      //                   comme dans le cas des Array[File] (intervention.photos par exemple)
    }
    // Cas 5: Tous les autres types (y compris les fichiers)
    else {
      formData.append(`${baseKey}[${key}]`, value)
    }
  })

  return formData
}

// Exemple d'utilisation
// const contacts = { contacts: [{ contact_id: 1 }, { contact_id: 2 }] }
// const formData = transformToFormData(contacts, 'document')

// Fonction pour vérifier si une valeur est un objet de fichier provenant du backend
export const isBackendFileObject = (value) => {
  return value !== null &&
         typeof value === 'object' &&
         !Array.isArray(value) &&
         !(value instanceof File) &&
         (
           (value.filename !== undefined && value.url !== undefined) ||
           value.attachable_sgid !== undefined
         );
};

// Fonction hasFiles modifiée pour fonctionner avec ou sans tableau fileFields
export const hasFiles = (fileFieldsOrData, data = null) => {
  // Si le second paramètre est null, alors le premier est les données
  // et nous devons détecter automatiquement les champs de fichier
  if (data === null) {
    const detectedFileFields = detectFileFields(fileFieldsOrData);
    return detectedFileFields.length > 0;
  }
  
  // Sinon, utiliser le comportement existant
  return fileFieldsOrData.some(
    fileAttr => {
      // Cas spécial du tableau de fichiers
      if (Array.isArray(data[fileAttr])) {
        // Vérifier si le tableau contient au moins un fichier
        const hasFiles = data[fileAttr].some(item => item instanceof File || isBackendFileObject(item))
        return hasFiles
      } else {
        // Cas normal pour les autres champs de fichier
        const isFile = data[fileAttr] instanceof File
        const isBackendFile = isBackendFileObject(data[fileAttr])
        // On ne supprime plus les champs qui ne sont pas des fichiers
        // car on veut conserver les valeurs null pour la suppression
        return isFile || isBackendFile || data[fileAttr] === null
      }
    }
  )
}
